<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { isValid, parse } from "chartjs-graph";
import { useBaseStore } from "@/stores/base";
import useDetectOutsideClick from "@/Utils/useDetectOutsideClick.ts";
import dayjs, { Dayjs } from "dayjs";
import DynamicDatesHelpDialog from "@/modules/explorer/components/DynamicDatesHelpDialog.vue";

const emit = defineEmits(["apply-date"]);

const emitApplyDates = () => {
	emit("apply-date");
};

const model = defineModel<string>();
const parsed = defineModel<Dayjs>("parsed", {
	default: () => ref(""),
});

watch(
	model,
	() => {
		parsed.value = parse(model.value, false);
	},
	{ immediate: true }
);

const isDateValid = computed(() => {
	return isValid(parsed.value);
});

const isMenuOpen = ref(false);
const datePickerMenu = ref();
const calendarIcon = ref(null);

useDetectOutsideClick(datePickerMenu, (event) => {
	if (!datePickerMenu.value?.$el.contains(event.target)) {
		nextTick(() => {
			isMenuOpen.value = false;
		});
	}
});

const datePickerValue = computed(() => {
	if (!isDateValid.value) {
		return null;
	}
	return parsed.value.toDate();
});

const updateDatePickerValue = (date) => {
	const parsedDate = dayjs(date);
	if (parsedDate.isSame(dayjs(), "day")) {
		model.value = "now";
	} else {
		model.value = parsedDate.format("YYYY-MM-DD") + " 00:00:00";
	}

	emitApplyDates();
	nextTick(() => {
		isMenuOpen.value = false;
	});
};

const formattedParsed = computed(() => {
	return model.value.includes("now")
		? parsed.value && isDateValid.value
			? parsed.value.format("YYYY-MM-DD HH:mm:ss")
			: ""
		: undefined;
});

const chackValidityAndApplyDates = () => {
	if (!isDateValid.value) {
		model.value = "now";
	}
	emitApplyDates();
};

const showDynamicDatesHelpDialog = ref(false);
</script>

<template>
	<dynamic-dates-help-dialog v-model="showDynamicDatesHelpDialog" />
	<v-text-field
		variant="filled"
		@blur="chackValidityAndApplyDates"
		v-model="model"
		hide-details="auto"
		:error-messages="isDateValid ? [] : 'Fill in a valid date or now'"
		:persistent-hint="model.includes('now')"
		:hint="formattedParsed"
		data-testid="snapshot-date-input-field"
	>
		<template v-slot:prepend>
			<div>now =</div>
		</template>
		<template v-slot:append>
			<v-btn
				size="24"
				border="0"
				variant="outlined"
				@click.stop="showDynamicDatesHelpDialog = true"
			>
				<v-icon size="16"> mdi-help-circle-outline</v-icon>
			</v-btn>
		</template>
		<template v-slot:append-inner>
			<v-menu
				v-model="isMenuOpen"
				:close-on-content-click="false"
				:attach="calendarIcon"
				ref="datePickerMenu"
				transition="scale-transition"
			>
				<template v-slot:activator="{ props }">
					<v-icon ref="calendarIcon" v-bind="props"> mdi-calendar</v-icon>
				</template>
				<v-card>
					<vc-date-picker
						show-iso-weeknumbers
						mode="date"
						style="border: 0"
						:first-day-of-week="2"
						:is-dark="useBaseStore().appTheme === 'DARK_BLUE_THEME'"
						:popover="{ placement: 'auto' }"
						:attributes="[{ key: 'today', dot: true, dates: new Date() }]"
						:model-value="datePickerValue"
						@update:modelValue="updateDatePickerValue"
						@click.stop
					/>
				</v-card>
			</v-menu>
		</template>
	</v-text-field>
</template>

<style scoped lang="scss"></style>
